import { StyleSheet, View } from 'react-native';
import { EsLoginContainer } from '../../components/ScreenContainer/EsLoginContainer';
import { useDispatchPromise } from '../../utils/AppUtils';
import { get2FATokenForLogin } from '../../redux/authentication/action';
import { useLocation } from 'react-router-dom';
import { getHomeRouteForView, normalizeDashboardView } from '../../utils/dashboardView';
import { login } from '../../redux/authentication/action';
import { TUsernamePassword } from '../../components/EsLoginForm/EsSigninData';
import ReCAPTCHA from 'react-google-recaptcha';
import { navigate } from '../../redux/navigator/action';
import { updateUserProfile } from '../../redux/user/actions';
import { isUpdatingProfile } from '../Dashboard/selectors';
import { useSelector } from 'react-redux';
import { useRef, useState } from 'react';
import { SentryCapture } from '../../analytics/Sentry';
import { claimPendingInfluencerIfAny } from '../../utils/influencerPrelink';

const BRAND_ROUTE = '/brand';

const styles = StyleSheet.create({
    container: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
});

const EsLogin = () => {
    const dispatch = useDispatchPromise();
    const updatingUserProfile = useSelector(isUpdatingProfile);
    const location = useLocation();
    const redirect = new URLSearchParams(location.search).get('redirect');
    const [loading, setLoading] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [resendInProgress, setResendInProgress] = useState(false);
    const [loginMessage, setLoginMessage] = useState<string | undefined>(undefined);
    const [loginChannel, setLoginChannel] = useState<'sms' | 'whatsapp' | 'audme' | 'email' | undefined>(undefined);
    const [audmeData, setAudmeData] = useState<{
        token?: string;
        qrCode?: string;
        deeplink?: string;
    } | null>(null);
    const ref = useRef<ReCAPTCHA>();

    const handleOnSubmit = async (
        data: TUsernamePassword,
        next: (() => void) | undefined,
        isLastStep?: boolean | undefined,
    ) => {
        ref.current?.reset();
        try {
            if (!isLastStep) {
                setLoading(true);
                const token = await ref.current?.executeAsync();
                data.token = token as unknown as string;
                dispatch(get2FATokenForLogin(data))
                    .then((response: any) => {
                        setLoading(false);
                        const message = typeof response === 'string' ? response : response?.message;
                        const user = response?.user;
                        const channel = response?.channel;
                        const { token, qrCode, deeplink } = response.audme || {};

                        // Store audme data if present in response
                        if (channel === 'audme' && token && qrCode && deeplink) {
                            setAudmeData({
                                token: token,
                                qrCode: qrCode,
                                deeplink: deeplink,
                            });
                        }
                        setLoginChannel(channel);

                        if (message && message === 'done') {
                            void (async () => {
                                await claimPendingInfluencerIfAny();
                                if (redirect) {
                                    if (redirect === BRAND_ROUTE) {
                                        await dispatch(updateUserProfile({ dashboard_view: 'brand' }));
                                    }
                                    dispatch(
                                        navigate({
                                            routes: redirect === '/profile' ? `/${user.userName}` : redirect,
                                            isRefreshed: true,
                                        }),
                                    );
                                } else {
                                    setLoading(true);
                                    const home = getHomeRouteForView(normalizeDashboardView(user?.dashboard_view));
                                    dispatch(
                                        navigate({
                                            routes: home,
                                            isRefreshed: true,
                                            skipAuthentication: true,
                                        }),
                                    );
                                }
                            })();
                        } else if (next) {
                            setLoading(false);
                            setLoginMessage(message);
                            setLoginChannel(channel);
                            next();
                        }
                    })
                    .catch(e => {
                        setLoading(false);
                    });
            } else {
                setOtpLoading(true);
                // Add quick_login_token if audme is selected and we have the token
                const loginPayload = { ...data };
                if (loginChannel === 'audme' && audmeData?.token) {
                    (loginPayload as any).quick_login_token = audmeData.token;
                }
                dispatch(login(loginPayload))
                    .then(async ({ user }: any) => {
                        setOtpLoading(false);
                        await claimPendingInfluencerIfAny();
                        if (redirect) {
                            if (redirect === BRAND_ROUTE) {
                                await dispatch(updateUserProfile({ dashboard_view: 'brand' }));
                            }
                            dispatch(
                                navigate({
                                    routes: redirect === '/profile' ? `/${user.userName}` : redirect,
                                    isRefreshed: true,
                                    skipAuthentication: true,
                                }),
                            );
                        } else {
                            setOtpLoading(false);
                            const home = getHomeRouteForView(normalizeDashboardView(user?.dashboard_view));
                            dispatch(
                                navigate({
                                    routes: home,
                                    isRefreshed: true,
                                    skipAuthentication: true,
                                }),
                            );
                        }
                    })
                    .catch(e => {
                        setOtpLoading(false);
                        SentryCapture(e);
                    });
            }
        } catch (error) {
            setOtpLoading(false);
        }
    };

    const handleResendOTP = (data: TUsernamePassword) => {
        setResendInProgress(true);
        return dispatch(get2FATokenForLogin({ ...data, resendToken: true }))
            .then(() => {
                setResendInProgress(false);
            })
            .catch(e => {
                console.log(e);
                setResendInProgress(false);
            });
    };

    return (
        <View style={styles.container}>
            <ReCAPTCHA sitekey={process.env.REACT_APP_CAPTCHA_KEY as string} size="invisible" ref={ref as any} />
            <EsLoginContainer
                onSubmit={handleOnSubmit}
                onResendOtp={handleResendOTP}
                loading={loading}
                otpLoading={otpLoading || updatingUserProfile}
                resendInProgress={resendInProgress}
                loginMessage={loginMessage}
                loginChannel={loginChannel}
                audmeData={audmeData}
            />
        </View>
    );
};

export { EsLogin };
